import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SpinningWheel.css';

const CARD_SPACING = 24;
const REPEAT_COUNT = 7;
const SPIN_DURATION = 7000;
const EASING = 'cubic-bezier(0.13, 0.9, 0.3, 1)';
const EXTRA_ROTATIONS = 3;

const SpinningWheel = ({ items = [], result = '', isSpinning = false, onSpinComplete }) => {
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  const [trackOffset, setTrackOffset] = useState(0);
  const [trackTransition, setTrackTransition] = useState('none');

  const duplicatedItems = useMemo(() => {
    if (!items.length) return [];
    const pool = [];
    for (let i = 0; i < REPEAT_COUNT; i++) {
      pool.push(...items);
    }
    return pool;
  }, [items]);

  useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, duplicatedItems.length);
  }, [duplicatedItems.length]);

  const targetIndex = useMemo(() => {
    if (!items.length || !result) return null;
    const resultIndex = items.findIndex(name => name === result);
    if (resultIndex === -1) return null;
    const middleSetStart = Math.floor(REPEAT_COUNT / 2) * items.length;
    return middleSetStart + resultIndex;
  }, [items, result]);

  const getOffsetForIndex = (index) => {
    const containerEl = containerRef.current;
    const cardEl = cardRefs.current[index];
    if (!containerEl || !cardEl) return 0;
    const containerWidth = containerEl.offsetWidth;
    const cardCenter = cardEl.offsetLeft + cardEl.offsetWidth / 2;
    return -(cardCenter - containerWidth / 2);
  };

  // Align when idle
  useEffect(() => {
    if (isSpinning || targetIndex == null) return;
    const offset = getOffsetForIndex(targetIndex);
    setTrackTransition('transform 500ms ease-out');
    setTrackOffset(offset);
  }, [isSpinning, targetIndex, duplicatedItems.length]);

  // Spin animation
  useEffect(() => {
    if (!isSpinning || targetIndex == null || !items.length) return;

    const totalItems = items.length;
    const randomShift = Math.floor(Math.random() * totalItems);
    const startIndex = targetIndex + (totalItems * EXTRA_ROTATIONS) + randomShift;
    const boundedStartIndex = Math.min(startIndex, duplicatedItems.length - 1);

    const startOffset = getOffsetForIndex(boundedStartIndex);
    const finalOffset = getOffsetForIndex(targetIndex);

    setTrackTransition('none');
    setTrackOffset(startOffset);

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTrackTransition(`transform ${SPIN_DURATION}ms ${EASING}`);
        setTrackOffset(finalOffset);
      });
    });

    const timeout = setTimeout(() => {
      onSpinComplete?.();
    }, SPIN_DURATION);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [isSpinning, targetIndex, duplicatedItems.length, items.length, onSpinComplete]);

  if (!items.length) return null;

  return (
    <div className="horizontal-scroll-container">
      <div className="scroll-window" ref={containerRef}>
        <div
          className={`name-scroll ${isSpinning ? 'scrolling' : ''}`}
          style={{
            transform: `translateX(${trackOffset}px)`,
            transition: trackTransition
          }}
        >
          {duplicatedItems.map((item, index) => (
            <div
              key={`${item}-${index}`}
              className={`name-card ${targetIndex === index && !isSpinning ? 'highlighted' : ''}`}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              style={{ marginRight: CARD_SPACING }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <div className="scroll-pointer"></div>
      {!isSpinning && result && (
        <div className="scroll-result">
          <div className="result-badge">{result}</div>
        </div>
      )}
    </div>
  );
};

export default SpinningWheel;
